import React from 'react';
import * as PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { Router, Link } from '../../router';
import { classNamer, capitalizeFirstLetter, findParentElement } from '../../lib/utils';
import i18n from '../../translations/i18n';
import { CloseIcon, LoadingAnimation } from '../../../static/icons/svg_icons';

import './KBSearchBar.scss';

const cn = classNamer('KBSearchBar');

const IGNORE_BACKDROP_CLICK_CLASSNAME = 'KBSearchBar__ignore-backdrop-click';

class KBSearchBar extends React.PureComponent {
  _mounted = false;
  searchListHovered = false;
  searchBarRef = React.createRef();
  INACTIVE_INDEX = -1;
  currentSearchTerm = '';

  state = {
    searchingForArticles: false,
    showSearchList: false,
    searchTerm: '',
    searchList: [],
    selectedSearchListItemIndex: -1,
  };

  componentDidMount() {
    this._mounted = true;
    if (this.props.focused) {
      this.searchBarRef.current.focus();
    }

    document.addEventListener('mousedown', this.onKBSearchBarBackdropEvent);
  }

  componentWillUnmount() {
    this._mounted = false;
    document.removeEventListener('mousedown', this.onKBSearchBarBackdropEvent);
  }

  onKBSearchBarBackdropEvent = e => {
    if (!this._mounted) {
      // Nothing to do
      return;
    }

    if (
      findParentElement(
        e.target,
        element => element.classList && element.classList.contains(IGNORE_BACKDROP_CLICK_CLASSNAME)
      )
    ) {
      // The click came through one of the elements we are ignoring.
      return;
    }

    this.setState({ showSearchList: false });
  };

  get isSearchTermValid() {
    return !!this.state.searchTerm.length;
  }

  searchKbArticlesByTitle = title => {
    const MAX_LIST_LENGTH = 10;

    if (this.isSearchTermValid) {
      this.props.onValidTerm(title).then(searchList => {
        this.setState({
          searchList: searchList.slice(0, MAX_LIST_LENGTH),
          showSearchList: true,
          searchingForArticles: false,
        });
      });
    }
  };
  debounceSearch = debounce(title => this.searchKbArticlesByTitle(title), 300);

  onChange = e => {
    const title = e.target.value;
    this.currentSearchTerm = title;
    this.setState(
      { searchTerm: title, searchingForArticles: true },
      () => title.trim() && this.debounceSearch(title.trim())
    );
  };

  onFocusIn = () => this.setState({ showSearchList: true });

  onSearchListMouseOver = e => {
    const { title, index } = e.target.dataset;
    if (title && index) {
      this.setState({ searchTerm: title, selectedSearchListItemIndex: Number(index) });
    }
  };

  onKeyDown = e => {
    const searchListLength = this.state.searchList.length;
    if (searchListLength) {
      if (e.keyCode === 38) {
        // up arrow

        this.setState(
          st => ({ selectedSearchListItemIndex: st.selectedSearchListItemIndex - 1 }),
          () => {
            if (this.state.selectedSearchListItemIndex < 0) {
              this.setState({
                selectedSearchListItemIndex: searchListLength,
                searchTerm: this.currentSearchTerm,
              });
            } else {
              this.setState({
                searchTerm: this.state.searchList[this.state.selectedSearchListItemIndex].title,
              });
            }
          }
        );
      }

      if (e.keyCode === 40) {
        // down arrow
        this.setState(
          st => ({ selectedSearchListItemIndex: st.selectedSearchListItemIndex + 1 }),
          () => {
            if (this.state.selectedSearchListItemIndex >= searchListLength) {
              this.setState({
                selectedSearchListItemIndex: this.INACTIVE_INDEX,
                searchTerm: this.currentSearchTerm,
              });
            } else {
              this.setState({
                searchTerm: this.state.searchList[this.state.selectedSearchListItemIndex].title,
              });
            }
          }
        );
      }

      if (
        // enter key
        e.keyCode === 13 &&
        this.state.selectedSearchListItemIndex !== this.INACTIVE_INDEX &&
        this.state.selectedSearchListItemIndex !== searchListLength
      ) {
        const article = this.state.searchList[this.state.selectedSearchListItemIndex];
        const route = this.props.getRoute(article);
        Router.push(route);
      }
    }

    if (
      // esc key
      e.keyCode === 27
    ) {
      this.setState(
        {
          searchTerm: this.currentSearchTerm,
          showSearchList: false,
        },
        () => {
          this.searchBarRef.current.blur();
        }
      );
    }
  };

  renderSearchedList() {
    const { searchList, showSearchList } = this.state;

    if (this.isSearchTermValid) {
      // We are searching for articles
      if (this.state.searchingForArticles) {
        return (
          <ul className={`search-list ${IGNORE_BACKDROP_CLICK_CLASSNAME}`}>
            <li className="search-list-item searching">{i18n.searchingForArticles}</li>
          </ul>
        );
      }

      // We have search results
      if (searchList.length && showSearchList) {
        return (
          <ul
            className={`search-list ${IGNORE_BACKDROP_CLICK_CLASSNAME}`}
            onMouseOver={this.onSearchListMouseOver}
          >
            {searchList.map((article, index) => (
              <li
                key={article.id}
                className={`search-list-item ${
                  this.state.selectedSearchListItemIndex === index ? 'selected' : ''
                }`}
              >
                <Link route={this.props.getRoute(article)}>
                  <a data-title={article.title} data-index={index}>
                    {article.title}
                    {article.category && Object.keys(article.category).length > 0 ? (
                      <span> ({capitalizeFirstLetter(article.category.name)})</span>
                    ) : (
                      <span className="text-danger">({i18n.uncategorized})</span>
                    )}
                    {!article.published_at && (
                      <span className="text-danger">({i18n.unpublished})</span>
                    )}
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        );
      }

      // No search results
      if (!searchList.length && showSearchList) {
        return (
          <ul className={`search-list ${IGNORE_BACKDROP_CLICK_CLASSNAME}`}>
            <li className="search-list-item no-results">
              <span>{i18n.searchKbArticlesNoResult}</span>
              <br />
              <span>{i18n.searchKbArticlesTryNewTerm}</span>
            </li>
          </ul>
        );
      }
    }
    return null;
  }

  render() {
    return (
      <div className={`${cn('search')} ${this.props.className ? this.props.className : ''}`}>
        <div className="search-wrapper">
          <input
            className={IGNORE_BACKDROP_CLICK_CLASSNAME}
            spellCheck="false"
            ref={this.searchBarRef}
            onKeyDown={this.onKeyDown}
            onChange={this.onChange}
            onFocus={this.onFocusIn}
            value={this.state.searchTerm}
            type="text"
            placeholder={i18n.quickFind}
          />
          {this.state.searchTerm &&
            (this.state.searchingForArticles ? (
              <LoadingAnimation className="loading-animation" />
            ) : (
              <CloseIcon onClick={() => this.setState({ searchTerm: '' })} className="close-icon" />
            ))}

          {this.renderSearchedList()}
        </div>
      </div>
    );
  }
}

KBSearchBar.propTypes = {
  className: PropTypes.string,
  getRoute: PropTypes.func,
  onValidTerm: PropTypes.func,
  focused: PropTypes.bool,
};

export default KBSearchBar;
